import { supabase } from '../lib/supabase';

export const dealershipPricingService = {
  // Get all dealership service pricing with optional filters
  async getAllDealershipPricing(filters = {}) {
    try {
      // Get all dealership pricing records
      let query = supabase
        .from('dealership_service_pricing')
        .select(`
          *,
          dealership:car_houses!dealership_id(id, name, code, city),
          service_type:service_types!service_type_id(id, name, display_name, description, price)
        `)
        .order('dealership_id', { ascending: true })
        .order('service_type_id', { ascending: true });

      // Apply filters
      if (filters?.dealership_id) {
        query = query.eq('dealership_id', filters.dealership_id);
      }
      if (filters?.service_type_id) {
        query = query.eq('service_type_id', filters.service_type_id);
      }
      if (filters?.is_active !== undefined) {
        query = query.eq('is_active', filters.is_active);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Transform the data to match the expected format
      const transformedData = data?.map(item => ({
        dealership_id: item.dealership_id,
        dealership_name: item.dealership?.name || 'Unknown Dealership',
        dealership_code: item.dealership?.code || '',
        dealership_city: item.dealership?.city || '',
        service_type_id: item.service_type_id,
        service_type_name: item.service_type?.name || 'Unknown Service',
        service_type_display_name: item.service_type?.display_name || 'Unknown Service',
        service_type_description: item.service_type?.description || '',
        final_price: item.price,
        dealership_price: item.price,
        default_price: item.service_type?.price || 0,
        pricing_type: 'custom',
        pricing_active: item.is_active,
        service_type_active: true
      })) || [];

      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Error fetching dealership pricing:', error);
      return { data: null, error };
    }
  },

  // Get pricing for a specific dealership
  async getDealershipPricing(dealershipId, filters = {}) {
    try {
      // First get all service types
      const { data: serviceTypes, error: serviceTypesError } = await supabase
        .from('service_types')
        .select('id, name, display_name, description, price')
        .order('name', { ascending: true });

      if (serviceTypesError) throw serviceTypesError;

      // Then get custom pricing for this dealership
      const { data: customPricing, error: pricingError } = await supabase
        .from('dealership_service_pricing')
        .select('*')
        .eq('dealership_id', dealershipId);

      if (pricingError) throw pricingError;

      // Create a map of custom pricing
      const pricingMap = new Map();
      customPricing?.forEach(item => {
        pricingMap.set(item.service_type_id, item);
      });

      // Combine service types with custom pricing or default pricing
      const combinedData = serviceTypes?.map(serviceType => {
        const customPricingItem = pricingMap.get(serviceType.id);
        
        return {
          dealership_id: dealershipId,
          dealership_name: 'Current Dealership', // Will be filled by the calling component
          dealership_code: '',
          dealership_city: '',
          service_type_id: serviceType.id,
          service_type_name: serviceType.name,
          service_type_display_name: serviceType.display_name,
          service_type_description: serviceType.description,
          final_price: customPricingItem ? customPricingItem.price : serviceType.price,
          dealership_price: customPricingItem ? customPricingItem.price : null,
          default_price: serviceType.price,
          pricing_type: customPricingItem ? 'custom' : 'default',
          pricing_active: customPricingItem ? customPricingItem.is_active : true,
          service_type_active: true
        };
      }) || [];

      // Apply filters
      let filteredData = combinedData;
      if (filters?.is_active !== undefined) {
        filteredData = filteredData.filter(item => item.pricing_active === filters.is_active);
      }
      if (filters?.search) {
        const searchTerm = filters.search.toLowerCase();
        filteredData = filteredData.filter(item => 
          item.service_type_display_name.toLowerCase().includes(searchTerm) ||
          item.service_type_name.toLowerCase().includes(searchTerm)
        );
      }

      return { data: filteredData, error: null };
    } catch (error) {
      console.error('Error fetching dealership pricing:', error);
      return { data: null, error };
    }
  },

  // Get pricing for a specific service type across all dealerships
  async getServiceTypePricing(serviceTypeId, filters = {}) {
    try {
      const { data, error } = await this.getAllDealershipPricing({
        service_type_id: serviceTypeId,
        ...filters
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching service type pricing:', error);
      return { data: null, error };
    }
  },

  // Create or update dealership service pricing
  async upsertDealershipPricing(dealershipId, serviceTypeId, pricingData) {
    try {
      const { data, error } = await supabase
        .from('dealership_service_pricing')
        .upsert({
          dealership_id: dealershipId,
          service_type_id: serviceTypeId,
          price: pricingData.price,
          is_active: pricingData.is_active !== undefined ? pricingData.is_active : true
        }, {
          onConflict: 'dealership_id,service_type_id'
        })
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error upserting dealership pricing:', error);
      return { data: null, error };
    }
  },

  // Update dealership service pricing
  async updateDealershipPricing(id, pricingData) {
    try {
      const { data, error } = await supabase
        .from('dealership_service_pricing')
        .update(pricingData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating dealership pricing:', error);
      return { data: null, error };
    }
  },

  // Delete dealership service pricing
  async deleteDealershipPricing(id) {
    try {
      const { error } = await supabase
        .from('dealership_service_pricing')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return { data: true, error: null };
    } catch (error) {
      console.error('Error deleting dealership pricing:', error);
      return { data: null, error };
    }
  },

  // Bulk update pricing for a dealership
  async bulkUpdateDealershipPricing(dealershipId, pricingUpdates) {
    try {
      const updates = pricingUpdates.map(update => ({
        dealership_id: dealershipId,
        service_type_id: update.service_type_id,
        price: update.price,
        is_active: update.is_active !== undefined ? update.is_active : true
      }));

      const { data, error } = await supabase
        .from('dealership_service_pricing')
        .upsert(updates, {
          onConflict: 'dealership_id,service_type_id'
        })
        .select();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error bulk updating dealership pricing:', error);
      return { data: null, error };
    }
  },

  // Reset pricing to default for a dealership
  async resetDealershipPricingToDefault(dealershipId) {
    try {
      const { error } = await supabase
        .from('dealership_service_pricing')
        .delete()
        .eq('dealership_id', dealershipId);

      if (error) throw error;

      return { data: true, error: null };
    } catch (error) {
      console.error('Error resetting dealership pricing:', error);
      return { data: null, error };
    }
  },

  // Get pricing summary for a dealership
  async getDealershipPricingSummary(dealershipId) {
    try {
      const { data, error } = await this.getDealershipPricing(dealershipId);

      if (error) throw error;

      if (!data || data.length === 0) {
        return { data: null, error: null };
      }

      const summary = {
        dealership_id: dealershipId,
        dealership_name: data[0].dealership_name,
        total_services: data.length,
        custom_pricing_count: data.filter(item => item.pricing_type === 'custom').length,
        default_pricing_count: data.filter(item => item.pricing_type === 'default').length,
        total_value: data.reduce((sum, item) => sum + parseFloat(item.final_price || 0), 0),
        pricing_data: data
      };

      return { data: summary, error: null };
    } catch (error) {
      console.error('Error fetching dealership pricing summary:', error);
      return { data: null, error };
    }
  },

  // Validate pricing data
  validatePricingData(pricingData) {
    const errors = {};

    if (!pricingData.price || pricingData.price < 0) {
      errors.price = 'Preis muss größer oder gleich 0 sein';
    }

    if (pricingData.price && pricingData.price > 9999.99) {
      errors.price = 'Preis darf nicht größer als 9999.99 € sein';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Get default pricing data
  getDefaultPricingData() {
    return {
      price: 0.00,
      is_active: true
    };
  }
};
